import React, { useEffect } from 'react';
import authEvents from '../../auth/authEvents';
import { store } from '../../store';
import { logout as logoutAction } from '../../store/authSlice';
import { removeAuthToken, getAuthToken } from '../../auth/authUtils';

/**
 * AuthProvider — global session-expiry handler.
 *
 * The axios interceptor fires `sessionExpired` whenever a request returns
 * 401 and the token refresh fails. We respond by wiping the stale auth
 * state and hard-redirecting to /login with the current URL preserved as
 * a returnTo query param, so useLoginForm / useRegisterForm can send the
 * buyer back to where they were after a successful (re-)auth.
 *
 * No modal: a modal here was the cause of the "dark blank page" — its
 * backdrop rendered before its content, and AuthWrapper also fired a
 * Navigate, leaving the user staring at a black screen until they
 * cleared cookies. A direct location.href change is loud, simple, and
 * guarantees the SPA boots clean.
 */
const AuthProvider = ({ children }) => {
  useEffect(() => {
    const handleSessionExpired = ({ error } = {}) => {
      // 0. Decide whether this 401 deserves a redirect.
      //
      //    - had a token → real session death, redirect.
      //    - no token + non-GET (the user clicked Subscribe / Follow /
      //      Tip / etc.) → user intent was to do an authed thing,
      //      redirect to /login so they can sign in and we land them
      //      back via returnTo.
      //    - no token + GET → background poll on a public page (e.g.
      //      notifications check while viewing an anonymous profile).
      //      Do not redirect — the visitor wasn't trying to do anything
      //      authed, the app code just speculatively asked.
      const hadToken = !!getAuthToken();
      const method = (error?.config?.method || 'get').toLowerCase();
      if (!hadToken && method === 'get') return;

      // 1. Drop the stale token + any other auth-only storage. We
      //    deliberately do not localStorage.clear() — theme prefs,
      //    age-gate flag, language choice, etc. are unrelated to auth
      //    and should survive a session expiry.
      try { removeAuthToken(); } catch {}
      try { localStorage.removeItem('persist:auth'); } catch {}
      try { localStorage.removeItem('persist:root'); } catch {}
      try { localStorage.removeItem('impersonation_pending'); } catch {}

      // 2. Clear the Redux auth slice so anything still mounted reads
      //    "logged out" until the redirect lands.
      try { store.dispatch(logoutAction()); } catch {}

      // 3. If we're already on an auth page, skip the redirect — the
      //    user is mid-login and a 401 just means their credentials
      //    were wrong (the login form handles that itself). Redirecting
      //    here would yank them off their own form mid-typing.
      const path = window.location.pathname;
      const onAuthPage = ['/login', '/register', '/forgot-password', '/reset-password', '/logout']
        .some((p) => path === p || path.startsWith(p + '/'));
      if (onAuthPage) return;

      // 4. Stash where they were as returnTo so useLoginForm /
      //    useRegisterForm can land them back after a successful auth.
      const returnTo = path + window.location.search;

      // 5. Hard redirect — full reload guarantees the new tree boots
      //    against a clean Redux + storage state.
      window.location.href = '/login?returnTo=' + encodeURIComponent(returnTo);
    };

    const unsub = authEvents.on('sessionExpired', handleSessionExpired);
    return () => unsub();
  }, []);

  return <>{children}</>;
};

export default AuthProvider;
